import {
  CalculatorIcon,
  CheckCircleIcon,
  HourglassMediumIcon,
  InfoIcon,
  PaperPlaneTiltIcon,
  StorefrontIcon,
} from '@phosphor-icons/react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  bargainingLetter,
  conditionKey,
  dealerAgreed,
  findingKindKey,
  questionKey,
  quoteFor,
  rejectedFields,
  type CheckFinding,
  type Mandate,
  type NegotiationDetail,
  type Offer,
} from '@entities/negotiation';
import type { FeeEstimate } from '@entities/success-fee';
import { ROUTES } from '@shared/config/routes';
import { money, moneyOf, type MoneyValue } from '@shared/lib/money';
import { percentFromBps } from '@shared/lib/percent';
import { Avatar, NextStepAction } from '@shared/ui';

import type { FeeTerms } from './BargainingSetup';
import styles from './BargainingFeed.module.css';

interface BargainingFeedProps {
  detail: NegotiationDetail;
  mandate: Mandate;
  findings: CheckFinding[];
  /** Последнее предложение автохауса с ценой; `null` — ответа с ценой ещё нет. */
  offer: Offer | null;
  /** Условия вознаграждения с сервера; `null` — контур выключен. */
  fee: FeeTerms | null;
  /**
   * Решение по предложению требуется прямо сейчас (`available_actions`):
   * `accept_offer` **или** `decline_offer`. Без согласия блок остаётся ради
   * отказа (BE-590).
   */
  decisionPending: boolean;
  /** Блоки решения — финальная сверка, «Согласиться / Отказаться», встречное предложение. */
  decision?: ReactNode;
}

/** Поля предложения в порядке показа: что автохаус сказал — по пунктам. */
const OFFER_FIELDS = [
  'price_cents',
  'total_cost_cents',
  'included_items',
  'warranty',
  'additional_costs',
  'handover',
  'valid_until',
  'is_final',
] as const;

/** Цена из ответа API или прочерк, если автохаус её не назвал. */
function priceOrDash(price: MoneyValue | null, locale: string): string {
  return price === null ? '—' : moneyOf(price, locale);
}

function formatTime(value: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { dateStyle: 'short', timeStyle: 'short' }).format(
    new Date(value),
  );
}

/**
 * Лента торга (FE-461, MVP_PRD §22.3, §22.7; решение заказчика от 12.09.2026).
 *
 * Переписки целиком здесь нет — копии писем у пользователя в почте. Экран
 * показывает **только разобранное Karl'ом**, в порядке событий:
 *
 * 1. **«Karl написал автохаусу»** — что именно: открытые пункты проверки как
 *    основание, максимум покупателя, условия, о которых Karl попросил, и
 *    вопросы покупателя. Пока письмо не ушло — так и сказано;
 * 2. **«Автохаус ответил»** — по пунктам, каждый с дословной цитатой: поле без
 *    цитаты на сервере не заполняется вовсе (§23.9), а отброшенные названы;
 * 3. **итог** — либо «автохаус согласен на ваши условия», либо «ниже X не
 *    отдаст»: было → стало, полученные условия и предварительный расчёт
 *    вознаграждения (§42.4). Дальше решение принимает человек — блоки
 *    решения приходят слотом `decision`;
 * 4. после согласия — контакты автохауса и что дальше: отдельный экран
 *    сопровождения (§39), сюда ведёт ссылка.
 *
 * **Три типа блоков, и они различимы без чтения подписей** (DESIGN_SYSTEM §7,
 * редизайн 2026-10):
 *
 * * **агент** — реплика Karl: фото со свечением, имя и маркировка
 *   «KI-Assistent», голубой пузырь `--color-bubble-karl` (§5.4);
 * * **продавец** — изолированная цитата: значок автохауса, нейтральная
 *   карточка, слова автохауса на подложке с линейкой и подпись «со слов
 *   автохауса, CarViQ.DE не проверял». Ни синего, ни красного: синий — это
 *   Karl и выбор, красный — главное действие, а слова продавца не то и не
 *   другое (§2);
 * * **система** — нейтральная подложка со значком: итог и состояние.
 *
 * Ни одного утверждения об автомобиле и продавце здесь нет: «согласен» значит
 * ровно «названная сумма не выше максимума», а не что сделка хороша (§2).
 */
export function BargainingFeed({
  detail,
  mandate,
  findings,
  offer,
  fee,
  decisionPending,
  decision,
}: BargainingFeedProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'de';
  const currency = detail.listing_price.currency;
  const letter = bargainingLetter(detail);
  const completed = detail.status === 'completed';
  const accepted = completed
    ? (detail.offers.find((item) => item.status === 'accepted') ?? null)
    : null;
  const dropped = offer === null ? [] : rejectedFields(offer);

  return (
    <div className={styles.feed}>
      <ol className={styles.timeline}>
        {/* --- 1. Что Karl написал — реплика агента ---------------------------- */}
        <li className={styles.item}>
          {/* Размер фото меняется медиазапросом, а у `Avatar` размер — свойство:
              в разметке два аватара, виден ровно один (как в `KarlIntro`). */}
          <span className={styles.avatarWide}>
            <Avatar variant="karl" size="md" />
          </span>
          <span className={styles.avatarNarrow}>
            <Avatar variant="karl" size="sm" />
          </span>
          <div className={styles.agentColumn}>
            <section className={styles.bubble} aria-label={t('negotiation.feed.wrote.title')}>
              {/* Маркировка AI: обязательна, не декоративна (MVP_PRD §26). */}
              <p className={styles.author}>
                <span className={styles.authorName}>{t('app.karl.name')}</span>
                <span className={styles.authorTag}>{t('app.karl.role')}</span>
              </p>
              <p className={styles.agentHead}>
                {/* Ожидание — песочные часы, а не знак спиннера: круглый
                    спиннер запрещён (DESIGN_SYSTEM §5.9). */}
                {letter !== null && letter.sent_at !== null ? (
                  <PaperPlaneTiltIcon size={18} aria-hidden="true" className={styles.headIcon} />
                ) : (
                  <HourglassMediumIcon size={18} aria-hidden="true" className={styles.headIcon} />
                )}
                <span>
                  {letter === null
                    ? t('negotiation.feed.wrote.drafting')
                    : letter.sent_at === null
                      ? t('negotiation.feed.wrote.scheduled')
                      : t('negotiation.feed.wrote.sent', {
                          when: formatTime(letter.sent_at, locale),
                        })}
                </span>
              </p>
              <p className={styles.lead}>{t('negotiation.feed.wrote.lead')}</p>
              <ul className={styles.points}>
                {findings.length === 0 ? null : (
                  <li>
                    <span className={styles.pointLabel}>
                      {t('negotiation.feed.wrote.findings')}
                    </span>
                    <ul className={styles.sublist}>
                      {findings.map((finding) => (
                        <li key={finding.code}>
                          {t(questionKey(finding.code))}
                          {' — '}
                          {t(findingKindKey(finding.kind))}
                        </li>
                      ))}
                    </ul>
                  </li>
                )}
                <li>
                  <span className={styles.pointLabel}>{t('negotiation.feed.wrote.maximum')}</span>
                  <span className={styles.maximum}>{moneyOf(mandate.target_price, locale)}</span>
                </li>
                {mandate.preferred_conditions.length === 0 ? null : (
                  <li>
                    <span className={styles.pointLabel}>
                      {t('negotiation.feed.wrote.conditions')}
                    </span>
                    <span>
                      {mandate.preferred_conditions.map((code) => t(conditionKey(code))).join(', ')}
                    </span>
                  </li>
                )}
                {mandate.custom_questions.length === 0 ? null : (
                  <li>
                    <span className={styles.pointLabel}>
                      {t('negotiation.feed.wrote.questions')}
                    </span>
                    <ul className={styles.sublist}>
                      {mandate.custom_questions.map((question) => (
                        <li key={question}>{question}</li>
                      ))}
                    </ul>
                  </li>
                )}
              </ul>
            </section>
            {/* Подпись экрана, а не реплика Karl: стоит под пузырём. */}
            <p className={styles.caption}>{t('negotiation.feed.wrote.copyNote')}</p>
          </div>
        </li>

        {/* --- 2. Что ответил автохаус — изолированная цитата ------------------ */}
        {offer === null ? null : (
          <li className={styles.item}>
            <span className={styles.marker} aria-hidden="true">
              <StorefrontIcon size={18} />
            </span>
            <section className={styles.dealer} aria-label={t('negotiation.feed.reply.title')}>
              <p className={styles.dealerHead}>
                {t('negotiation.feed.reply.title')}
                {' · '}
                <span className={styles.time}>{formatTime(offer.created_at, locale)}</span>
              </p>
              <ol className={styles.replyList}>
                {OFFER_FIELDS.map((field) => {
                  const quote = quoteFor(offer, field);
                  if (quote === null) return null;
                  return (
                    <li key={field} className={styles.replyItem}>
                      <span className={styles.pointLabel}>
                        {t(`negotiation.offerField.${field}`)}
                        {field === 'price_cents' && offer.price !== null ? (
                          <>
                            {': '}
                            <span className={styles.figure}>{moneyOf(offer.price, locale)}</span>
                          </>
                        ) : null}
                        {field === 'total_cost_cents' && offer.total_cost !== null ? (
                          <>
                            {': '}
                            <span className={styles.figure}>
                              {moneyOf(offer.total_cost, locale)}
                            </span>
                          </>
                        ) : null}
                      </span>
                      {/* Цитата — единственная форма существования ответа
                          автохауса; ссылки в ней не разбираются (§22.8). */}
                      <blockquote className={styles.quote}>{quote}</blockquote>
                    </li>
                  );
                })}
              </ol>
              <p className={styles.quoteNote}>{t('negotiation.decision.quoteDisclaimer')}</p>
              {dropped.length === 0 ? null : (
                <p className={styles.quoteNote}>
                  {t('negotiation.decision.dropped', {
                    fields: dropped
                      .map((item) => t(`negotiation.offerField.${item.field}`))
                      .join(', '),
                  })}
                </p>
              )}
            </section>
          </li>
        )}

        {/* --- 3. Итог — системный блок ---------------------------------------- */}
        {offer !== null && offer.price !== null && (decisionPending || completed) ? (
          <OutcomeCard
            offer={offer}
            mandate={mandate}
            listingCents={mandate.listing_price_snapshot.amount_cents}
            currency={currency}
            locale={locale}
            fee={fee}
            estimate={detail.fee_estimate}
            completed={completed}
          />
        ) : null}

        {/* --- 4. Согласие дано: контакты и что дальше — системный блок -------- */}
        {completed ? (
          <li className={styles.item}>
            <span className={styles.marker} aria-hidden="true">
              <CheckCircleIcon size={18} />
            </span>
            <section className={styles.system} aria-label={t('negotiation.feed.done.title')}>
              <p className={styles.systemTitle}>{t('negotiation.feed.done.title')}</p>
              <p className={styles.lead}>
                {t('negotiation.feed.done.lead', {
                  price: priceOrDash(accepted?.price ?? offer?.price ?? null, locale),
                })}
              </p>
              <p className={styles.lead}>{t('negotiation.feed.done.meetYourself')}</p>
              {detail.search_session_id === null ? null : (
                <div className={styles.actions}>
                  {/* Единственное действие экрана после согласия — красное
                      (DESIGN_SYSTEM §5.1): дальше путь идёт на шаг 7. */}
                  <NextStepAction
                    to={ROUTES.searchHandover(detail.search_session_id)}
                    dockLabel={t('negotiation.feed.done.contactsShort')}
                  >
                    {t('negotiation.feed.done.contacts')}
                  </NextStepAction>
                </div>
              )}
            </section>
          </li>
        ) : null}
      </ol>

      {/* Блоки решения — не событие ленты, а действие пользователя: стоят под
          итогом отдельными карточками. */}
      {decisionPending ? decision : null}
    </div>
  );
}

interface OutcomeCardProps {
  offer: Offer;
  mandate: Mandate;
  listingCents: number;
  currency: string;
  locale: string;
  fee: FeeTerms | null;
  /** Предварительное вознаграждение с сервера (`detail.fee_estimate`, BE-593). */
  estimate: FeeEstimate | null;
  completed: boolean;
}

/**
 * «Было → стало» и предварительный расчёт вознаграждения (§22.7, §42.4).
 *
 * Разница с объявлением — разница двух чисел с сервера: снимок цены объявления
 * в мандате и цена из предложения. **Вознаграждение экран не считает** (BE-593,
 * находка H-7): сумму присылает сервер той же функцией, что начислит её при
 * отметке «купил», — от подтверждённой стартовой цены, которая бывает ниже
 * объявления. Своя арифметика экрана обещала 50 €, а начислено было 100 €.
 * Сумма названа **предварительной**; стоимость подарков не оценивается никогда
 * (§42.3).
 *
 * Это **системный блок** ленты: нейтральная подложка и значок. Цена в пределах
 * максимума отмечена галочкой — это «соответствует вашему условию», и только
 * здесь зелёный уместен (DESIGN_SYSTEM §2.4); смысл несёт заголовок словами.
 * Расчёт вознаграждения — «Рассчитано CarViQ.DE»: тон `info` с иконкой.
 */
function OutcomeCard({
  offer,
  mandate,
  listingCents,
  currency,
  locale,
  fee,
  estimate,
  completed,
}: OutcomeCardProps) {
  const { t } = useTranslation();
  const agreed = dealerAgreed(offer, mandate);
  const priceCents = offer.price?.amount_cents ?? listingCents;
  const discount = Math.max(0, listingCents - priceCents);
  const extras = offer.included_items.length > 0;
  // Сумма — только к тому предложению, по которому сервер её посчитал.
  const shown = estimate !== null && estimate.offer_id === offer.offer_id ? estimate : null;
  const positive = completed || agreed;

  return (
    <li className={styles.item}>
      <span
        className={positive ? `${styles.marker} ${styles.markerMatch}` : styles.marker}
        aria-hidden="true"
      >
        {positive ? <CheckCircleIcon size={18} /> : <InfoIcon size={18} />}
      </span>
      <section className={styles.system} aria-label={t('negotiation.feed.outcome.title')}>
        <p className={styles.systemTitle}>
          {completed
            ? t('negotiation.feed.outcome.accepted')
            : agreed
              ? t('negotiation.feed.outcome.agreed')
              : t('negotiation.feed.outcome.notAgreed', {
                  price: priceOrDash(offer.price, locale),
                })}
        </p>
        <dl className={styles.compare}>
          <div>
            <dt>{t('negotiation.feed.outcome.was')}</dt>
            <dd className={styles.figure}>{moneyOf(mandate.listing_price_snapshot, locale)}</dd>
          </div>
          <div>
            <dt>{t('negotiation.feed.outcome.yourMaximum')}</dt>
            <dd className={styles.figure}>{moneyOf(mandate.target_price, locale)}</dd>
          </div>
          <div>
            <dt>{t('negotiation.feed.outcome.now')}</dt>
            <dd className={`${styles.figure} ${styles.strong}`}>
              {priceOrDash(offer.price, locale)}
            </dd>
          </div>
          <div>
            <dt>{t('negotiation.feed.outcome.discount')}</dt>
            <dd className={styles.figure}>{money(discount, currency, locale, 'auto')}</dd>
          </div>
          <div className={styles.extrasRow}>
            <dt>{t('negotiation.feed.outcome.extras')}</dt>
            <dd className={styles.extras}>
              {extras
                ? (quoteFor(offer, 'included_items') ?? t('negotiation.feed.outcome.extrasYes'))
                : t('negotiation.feed.outcome.extrasNone')}
            </dd>
          </div>
        </dl>
        {fee === null || shown === null ? null : (
          <p className={styles.fee}>
            <CalculatorIcon size={16} aria-hidden="true" className={styles.feeIcon} />
            <span>
              {t('negotiation.feed.outcome.fee', {
                fee: money(shown.amount_cents, currency, locale, 'auto'),
              })}{' '}
              {/* Стартовая цена ниже объявления — её назвал автохаус в
                  проверке, и от неё считается скидка (§42.3). */}
              {shown.baseline_source === 'dealer_confirmed_price'
                ? `${t('negotiation.feed.outcome.feeBaselineDealer', {
                    price: money(shown.baseline_price_cents, currency, locale, 'auto'),
                  })} `
                : null}
              {/* Ставка — та же, по которой посчитана сумма рядом: из
                  конфигурации, а не литералом в локали (FE-583). */}
              {t('negotiation.feed.outcome.feeNote', {
                rate: percentFromBps(fee.rate_bps, locale),
              })}
            </span>
          </p>
        )}
        {completed || agreed ? null : (
          <p className={styles.lead}>{t('negotiation.feed.outcome.notAgreedHint')}</p>
        )}
      </section>
    </li>
  );
}
